/**
 * FindGaps.js — Orchestrator
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box, Typography, useTheme,
} from '@mui/material';
import GapsInput from './gaps/GapsInput';
import GapsResults from './gaps/GapsResults';
import GapsEmptyState from './gaps/GapsEmptyState';
import { gapsApi } from '../services/api';
import PageLoader from './PageLoader';
import PageAlert from './PageAlert';

// ── Main component ────────────────────────────────────────────────────────────
const FindGaps = React.memo(({ onFindGaps, loading, gaps, error }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [yourContent, setYourContent] = useState('');
  const [compContent, setCompContent] = useState('');
  const [localError, setLocalError] = useState('');
  const [urlFetchAvailable, setUrlFetch] = useState(false);

  // Check backend capabilities once on mount
  useEffect(() => {
    gapsApi.health().then(h => setUrlFetch(h.url_fetch));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!yourContent.trim()) { setLocalError('Please enter your content or a URL'); return; }
    if (!compContent.trim()) { setLocalError('Please enter the competitor content or a URL'); return; }
    setLocalError('');
    onFindGaps(yourContent, compContent);
  }, [yourContent, compContent, onFindGaps]);

  const isValid = useMemo(
    () => !!(yourContent.trim() && compContent.trim() && !loading),
    [yourContent, compContent, loading],
  );

  // Shared card style — passed down so all result cards stay visually in sync
  const card = useMemo(() => ({
    p: { xs: 2.5, md: 4 },
    borderRadius: 5,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s ease',
    boxShadow: isDark
      ? '0 10px 40px rgba(0,0,0,0.5)'
      : '0 10px 40px rgba(0,0,0,0.03)',
  }), [theme, isDark]);

  const hasResult = !!(gaps && gaps.scores);

  if (loading && !hasResult) {
    return (
      <Box sx={{ py: 4 }}>
        <PageLoader type="grid" count={2} />
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Standard Page Header ── */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 1.5 }}>
          Gap Analysis
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          Compare your strategy against competitors to find untapped growth opportunities
        </Typography>
      </Box>

      {/* Error alerts */}
      <PageAlert error={error || localError} onClose={() => setLocalError('')} />

      {/* Input + submit */}
      <GapsInput
        yourContent={yourContent}
        onYourChange={e => { setYourContent(e.target.value); setLocalError(''); }}
        compContent={compContent}
        onCompChange={e => { setCompContent(e.target.value); setLocalError(''); }}
        onSubmit={handleSubmit}
        loading={loading}
        isValid={isValid}
        urlFetchAvailable={urlFetchAvailable}
        card={card}
      />

      {/* Results or empty state */}
      <Box sx={{ mt: 5 }}>
        {hasResult
          ? <GapsResults result={gaps} card={card} />
          : !loading && <GapsEmptyState />
        }
      </Box>
    </Box>
  );
});

export default FindGaps;